import React from 'react';

import { Desktop } from '@99designs/common/components/Desktop';
import { Icon } from '@99designs/common/components/Icon';
import { formatMessage } from '@99designs/common/i18n';
import { BriefDetails_StructuredBrief } from '__generated__/BriefDetails_StructuredBrief';
import { BriefEntry_User } from '__generated__/BriefEntry_User';
import { scrollToElement } from 'common/utils/scroll';
import BriefDetailsList from 'projects/components/BriefDetailsList';
import BriefEntryAction from 'projects/components/BriefEntryAction';
import Card from 'projects/components/Card';
import ColorComponent from 'projects/components/Color';
import ContentSetting, { Sizes } from 'projects/components/ContentSetting';
import Header from 'projects/components/Header';
import HeaderUserLayout from 'projects/components/HeaderUserLayout';
import { Matrix, MatrixItem } from 'projects/components/Matrix';
import { Color } from 'projects/consts';
import { trackBriefEntryViewedEvent } from 'projects/events';
import styled from 'styled-components';
import { BriefType } from '.';

export interface Props {
    designer: { displayName: string; id: number } | null;
    recentDrafts: BriefDetails_StructuredBrief[];
    recentLaunchedBriefs: BriefDetails_StructuredBrief[];
    createBriefAction: () => void;
    resumeBriefAction: (brief: BriefType) => void;
    copyBriefAction: (brief: BriefType) => void;
    user: BriefEntry_User;
}

export enum Selection {
    CREATE = 'create',
    RESUME = 'resume',
    COPY = 'copy',
}

interface State {
    currentSelection: Selection | null;
}

const Designer = styled.div`
    display: flex;
    justify-content: center;
`;

class BriefEntryPage extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            currentSelection: null,
        };
    }

    componentDidMount() {
        trackBriefEntryViewedEvent({ user_id: this.props.user.id });
    }

    toggleDisplayBriefs = (mode: Selection) => {
        if (mode !== this.state.currentSelection) {
            this.setState({
                currentSelection: mode,
            });
        } else {
            this.setState({
                currentSelection: null,
            });
        }
    };

    render() {
        const {
            designer,
            user,
            createBriefAction,
            recentDrafts,
            resumeBriefAction,
            recentLaunchedBriefs,
            copyBriefAction,
        } = this.props;

        const contentSettingSize = !(recentDrafts.length && recentLaunchedBriefs.length)
            ? Sizes.Small
            : undefined;
        const matrixSize = recentDrafts.length && recentLaunchedBriefs.length ? 3 : 2;

        return (
            <>
                <ContentSetting size={contentSettingSize}>
                    <Header
                        title={__("Hey, let's get started | 99designs")}
                        textAlignment={'center'}
                    >
                        <h2 className="heading heading--h2">
                            {formatMessage(__("Hey {displayName}, let's get started"), {
                                displayName: user.displayName,
                            })}
                        </h2>
                        {designer && (
                            <Designer>
                                <HeaderUserLayout
                                    users={[{ id: designer.id, displayName: designer.displayName }]}
                                />
                            </Designer>
                        )}
                    </Header>

                    <div className="brief-actions-layout">
                        <Matrix
                            columnOverrides={{
                                large: matrixSize,
                                normal: matrixSize,
                                medium: 1,
                            }}
                        >
                            {recentLaunchedBriefs.length > 0 && (
                                <MatrixItem>
                                    <Card
                                        onClick={() => this.toggleDisplayBriefs(Selection.COPY)}
                                        variations={['clickable', 'hoverable'].concat(
                                            this.state.currentSelection == Selection.COPY
                                                ? ['selected']
                                                : []
                                        )}
                                    >
                                        <BriefEntryAction
                                            url={require('images/projects/create/copy_brief.svg')}
                                            title={__('Copy a previous project')}
                                        />
                                        {this.state.currentSelection == Selection.COPY && (
                                            <Desktop>
                                                <Card.Icon>
                                                    <ColorComponent color={Color.createBlue}>
                                                        <Icon type={'tick-3'} variations={['3x']} />
                                                    </ColorComponent>
                                                </Card.Icon>
                                            </Desktop>
                                        )}
                                    </Card>
                                </MatrixItem>
                            )}
                            {recentDrafts.length > 0 && (
                                <MatrixItem>
                                    <Card
                                        onClick={() => this.toggleDisplayBriefs(Selection.RESUME)}
                                        variations={['clickable', 'hoverable'].concat(
                                            this.state.currentSelection == Selection.RESUME
                                                ? ['selected']
                                                : []
                                        )}
                                    >
                                        <BriefEntryAction
                                            url={require('images/projects/create/continue_brief.svg')}
                                            title={__('Continue with your draft')}
                                        />
                                        {this.state.currentSelection == Selection.RESUME && (
                                            <Desktop>
                                                <Card.Icon>
                                                    <ColorComponent color={Color.createBlue}>
                                                        <Icon type={'tick-3'} variations={['3x']} />
                                                    </ColorComponent>
                                                </Card.Icon>
                                            </Desktop>
                                        )}
                                    </Card>
                                </MatrixItem>
                            )}
                            <MatrixItem>
                                <Card
                                    onClick={() => {
                                        this.toggleDisplayBriefs(Selection.CREATE);
                                        createBriefAction();
                                    }}
                                    variations={['clickable', 'hoverable']}
                                >
                                    <BriefEntryAction
                                        url={require('images/projects/create/new_brief.svg')}
                                        title={__('Start a new brief')}
                                    />
                                </Card>
                            </MatrixItem>
                        </Matrix>
                    </div>
                    <div ref={ref => ref && scrollToElement(ref, 15, 300)}>
                        {this.state.currentSelection === Selection.RESUME && (
                            <Card>
                                <BriefDetailsList
                                    drafts={recentDrafts}
                                    onClick={resumeBriefAction}
                                    heading={__('Choose one of your most recent drafts')}
                                />
                            </Card>
                        )}
                        {this.state.currentSelection === Selection.COPY && (
                            <Card>
                                <BriefDetailsList
                                    drafts={recentLaunchedBriefs}
                                    onClick={copyBriefAction}
                                    heading={__('Choose one of your most recent projects')}
                                />
                            </Card>
                        )}
                    </div>
                </ContentSetting>
            </>
        );
    }
}

export default BriefEntryPage;
